import {
  ImportStatus,
  DataSourceType,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceData } from "@syla/shared/types/responses/TransactionEvent";
import { ImportMethod } from "../../components/organisms/dataSource/panels/ImportMethodSelectionPanel";
import { DataSourceInputType } from "./DataSourceInputType";

type DataSourceInstructions =
  | {
      importMethod: DataSourceInputType.Custom;
      dataSource?: undefined;
      isNewDataSource: boolean;
    }
  | {
      importMethod: ImportMethod;
      dataSource: DataSourceData;
      isNewDataSource: boolean;
    };

export type DataSourceInstructionVariables = {
  importMethod: ImportMethod;
  dataSourceType?: DataSourceType;
  dataSourceName?: string;
  dataSourceWebsiteUrl?: string;
};

export type DataSourceInstruction = {
  content: string; // the markdoc template to render
  variables: DataSourceInstructionVariables;
};

export function getDataSourceInstruction(
  data: DataSourceInstructions,
): DataSourceInstruction {
  const variables = {
    importMethod: data.importMethod,
    dataSourceName: data.dataSource?.name,
    dataSourceType: data.dataSource?.type,
    dataSourceWebsiteUrl: data.dataSource?.url,
  };

  switch (data.importMethod) {
    case "Empty":
      return {
        content: createEmptyDataSourceInstruction,
        variables,
      };
    case DataSourceInputType.Custom:
      if (data.dataSource?.type === DataSourceType.Exchange) {
        return {
          content: customImportInstructionForExchange,
          variables,
        };
      }

      if (
        data.dataSource?.type === DataSourceType.Custom &&
        data.isNewDataSource
      ) {
        return {
          content: customDataSourceImportInstruction,
          variables,
        };
      }

      return {
        content: customImportInstruction,
        variables,
      };
    case DataSourceInputType.Api:
      if (data.dataSource.syncImportInstructions) {
        return {
          content: replaceTokens({
            text: data.dataSource.syncImportInstructions,
            dataSource: data.dataSource,
          }),
          variables,
        };
      }

      return {
        content: replaceTokens({
          text: defaultApiInstructions,
          dataSource: data.dataSource,
        }),
        variables,
      };

    case DataSourceInputType.Csv:
      if (data.dataSource.fileImportInstructions) {
        return {
          content: replaceTokens({
            text: data.dataSource.fileImportInstructions,
            dataSource: data.dataSource,
          }),
          variables,
        };
      }

      if (data.dataSource.fileImportStatus === ImportStatus.Alpha) {
        return {
          content: replaceTokens({
            text: defaultCsvAssistedImportInstructions,
            dataSource: data.dataSource,
          }),
          variables,
        };
      }

      return {
        content: replaceTokens({
          text: defaultCsvInstructions,
          dataSource: data.dataSource,
        }),
        variables,
      };
  }
}

const defaultApiInstructions = `{% instructionHeading /%}

{% stepsHeading /%}

1. {% loginStep /%}
1. Create a **read-only API key** that allows Syla to access your transaction history.
1. Copy the API key details into Syla.
1. In Syla, click {% importAction /%}.`;

const defaultCsvAssistedImportInstructions = `## How to request an Assisted File Import

{% stepsHeading /%}

1. {% loginStep /%}
1. Export your **complete transaction history** including your trades, deposits, withdrawals and any other transactions.
1. In Syla, click **Request Assisted Import** and upload your transaction files.
1. Our team will review the files and keep you updated on the progress of your import.
1. You can also follow our [Detailed Instructions](https://help.syla.com.au/en/articles/8328641-assisted-file-import).`;

const defaultCsvInstructions = `{% instructionHeading /%}

{% stepsHeading /%}

1. {% loginStep /%}
1. Export your complete transaction history including your trades, deposits, withdrawals and any other transactions.
1. Upload the files into Syla, then click {% importAction /%}.`;

const customImportInstruction = `## How to manually import your {% $dataSourceName %} transactions

{% stepsHeading /%}

1. Download the [Custom Ledgers CSV Template](/custom_ledgers_csv_template.csv).
1. Follow the [Detailed Instructions](https://help.syla.com.au/en/articles/8281571-custom-csv-template-import-advanced) to enter your transactions into the template.
1. Upload the completed template into Syla, then click {% importAction /%}.`;

const customImportInstructionForExchange =
  customImportInstruction +
  `
{% alert type="tip" %}
If a file downloaded from the exchange does not import, let us know. We can often add direct support for that file format.
{% /alert %}`;

// Add additional tips for the user if they are creating a custom data source.
// This usually only happens when they haven't found their exchange / wallet,
// but it might be a typo etc.
const customDataSourceImportInstruction = `
## How to manually import your {% $dataSourceName %} transactions

{% alert type="info" %}
You’re creating a custom unsupported data source. Please check if your platform is already supported in Syla so the original file can be imported directly.
{% /alert %}

{% alert type="tip" %}
If your exchange isn’t supported in Syla yet, send the original transaction file to our support team and we’ll check if a direct import can be added.
{% /alert %}

{% stepsHeading /%}

1. Download the [Custom Ledgers CSV Template](/custom_ledgers_csv_template.csv).
1. Follow the [Detailed Instructions](https://help.syla.com.au/en/articles/8281571-custom-csv-template-import-advanced) to enter your transactions into the template.
1. Upload the completed template into Syla, then click {% importAction /%}.
`;

const createEmptyDataSourceInstruction = `## How to create an empty data source

{% stepsHeading /%}

1. Enter the **Data source name**.
1. Click **Create**.`;

const replaceTokens = ({
  text,
  dataSource,
}: {
  text: string;
  dataSource: DataSourceData;
}): string => {
  return text.replace(/{name}/g, dataSource.name);
};
